import { Save } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { TooltipProvider, WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { describeCombo } from '@/lib/keys'
import { useEditorStore } from '@/stores/editorStore'

import { describeSaveError, useSaveController } from './useAutosave'

/**
 * Save status + **Save** + **Discard changes**, for the header of either editor shell. Reads the
 * editor store; needs no props.
 *
 * Nothing saves by itself: the status says "Unsaved changes" until the person presses Save (or
 * Ctrl+S). Discard puts the picture back to the last saved version after a confirm, and is itself
 * undoable. A `409` (someone else saved a newer version) opens the conflict dialog here, in both
 * shells, with three plain choices.
 */

export interface SaveControlsProps {
  className?: string
  /** Hide the status words (a narrow header); the Save button still says what it does. */
  hideStatus?: boolean
}

function clock(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export function SaveControls({ className, hideStatus = false }: SaveControlsProps = {}) {
  const ctl = useSaveController()
  const hasDoc = useEditorStore((s) => s.doc !== null && s.meta !== null)
  const gesturing = useEditorStore((s) => s.gesturing)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  /** The conflict (by the newer rev) the person chose to "decide later" about. */
  const [dismissedRev, setDismissedRev] = useState<number | null>(null)

  if (!hasDoc) return null

  const conflict = ctl.conflict
  const conflictOpen = conflict !== null && dismissedRev !== conflict.current_rev
  const otherError = ctl.error && ctl.error.kind !== 'conflict' ? ctl.error : null

  let status: string
  let tone: 'muted' | 'warn' | 'danger' | 'ok' = 'muted'
  if (ctl.saving) status = 'Saving…'
  else if (conflict) {
    status = 'Not saved — a newer version exists'
    tone = 'danger'
  } else if (otherError) {
    status = 'Could not save'
    tone = 'danger'
  } else if (ctl.dirty) {
    status = 'Unsaved changes'
    tone = 'warn'
  } else if (ctl.savedAt) {
    status = `Saved at ${clock(ctl.savedAt)}`
    tone = 'ok'
  } else status = 'No changes yet'

  const saveLabel = ctl.saving ? 'Saving…' : otherError && ctl.dirty ? 'Try again' : 'Save'

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('flex items-center gap-2', className)} data-testid="save-controls">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap text-xs',
            tone === 'warn' ? 'font-medium text-ink' : tone === 'danger' ? 'font-medium text-danger' : 'text-ink-3',
            hideStatus && 'sr-only',
          )}
          aria-live="polite"
          title={describeSaveError(ctl.error) ?? undefined}
          data-testid="save-status"
        >
          {tone === 'warn' ? <span className="size-2 rounded-full bg-warn" aria-hidden /> : null}
          {status}
        </span>

        {conflict && !conflictOpen ? (
          <Button variant="link" size="sm" onClick={() => setDismissedRev(null)}>
            Choose a version
          </Button>
        ) : null}

        {ctl.dirty && !ctl.saving ? (
          // A phone says "Discard" so Save still fits on the same line (the name stays whole).
          <Button variant="ghost" size="sm" disabled={gesturing} onClick={() => setConfirmDiscard(true)} aria-label="Discard changes">
            <span className="sm:hidden" aria-hidden>
              Discard
            </span>
            <span className="hidden sm:inline" aria-hidden>
              Discard changes
            </span>
          </Button>
        ) : null}

        <WithTooltip label={`Save (${describeCombo('$mod+s')})`}>
          <Button
            variant="primary"
            size="sm"
            loading={ctl.saving}
            disabled={!ctl.dirty || ctl.saving || conflict !== null}
            onClick={() => void ctl.save()}
          >
            {ctl.saving ? null : <Save aria-hidden />}
            {saveLabel}
          </Button>
        </WithTooltip>

        <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
          <DialogContent size="sm" hideClose>
            <DialogHeader>
              <DialogTitle>Throw away your unsaved changes?</DialogTitle>
              <DialogDescription>
                The picture goes back to how it was when you last saved. If you change your mind, press Undo to bring your changes back.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setConfirmDiscard(false)} autoFocus>
                Keep editing
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  ctl.discard()
                  setConfirmDiscard(false)
                }}
              >
                Throw them away
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={conflictOpen} onOpenChange={(open) => (!open && conflict ? setDismissedRev(conflict.current_rev) : undefined)}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Someone else changed this picture</DialogTitle>
              <DialogDescription>
                While you were working, a newer version of this picture was saved — by an automatic run, or in another tab. Which one do you want to keep?
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2 text-sm">
              <Button variant="primary" loading={ctl.saving} onClick={() => void ctl.keepMine()}>
                Keep my version
              </Button>
              <p className="-mt-1 text-xs text-ink-2">Saves what you see now over the newer version.</p>
              <Button variant="secondary" disabled={ctl.saving} onClick={() => void ctl.reloadTheirs()}>
                Use the newer version
              </Button>
              <p className="-mt-1 text-xs text-ink-2">Your changes are put aside — press Undo to get them back.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => conflict && setDismissedRev(conflict.current_rev)}>
                Decide later
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  )
}
